import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Button, IconButton,
  CircularProgress, Alert, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Stack, MenuItem, useMediaQuery, useTheme,
  Tabs, Tab, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Checkbox, ListItemText, Select, FormControl, OutlinedInput
} from '@mui/material';
import { Close as CloseIcon,
  AutoAwesome as AutoIcon, Add as AddIcon, CheckCircle as ConfirmIcon,
  Delete as DeleteIcon, CalendarMonth as CalendarIcon, Refresh as RefreshIcon,
  Warning as WarningIcon, CheckCircleOutline as OkIcon, Error as ErrorIcon,
  Edit as EditIcon, RestartAlt as ResetIcon, FlashOn as ShootTodayIcon,
  ContentCopy as CopyIcon, Save as SaveIcon, PictureAsPdf as PdfIcon,
  Fullscreen as FullscreenIcon, FullscreenExit as FullscreenExitIcon,
  ArrowUpward as MoveUpIcon, ArrowDownward as MoveDownIcon,
  FreeBreakfast as CoffeeIcon
} from '@mui/icons-material';
import api from '../services/api';
import { formatDate } from '../utils/dateFormatter';

const STATUS_CHIP = {
  all_available: { color: 'success', icon: <OkIcon fontSize="small" />,     label: 'All Available' },
  overloaded:    { color: 'warning', icon: <WarningIcon fontSize="small" />, label: 'Day Limit Exceeded' },
  partial:       { color: 'warning', icon: <WarningIcon fontSize="small" />, label: 'Partial Conflict' },
  conflict:      { color: 'error',   icon: <ErrorIcon fontSize="small" />,   label: 'Conflict' },
  unchecked:     { color: 'default', icon: null,                             label: 'Not Checked' }
};

const CONTENT_TYPE_COLOR = { 'Long Video': '#1565C0', 'Short Video': '#E65100', 'Ad': '#2E7D32' };

const BORDER_COLOR = {
  all_available: 'success.light',
  overloaded: 'warning.light',
  partial: 'warning.light',
  conflict: 'error.light',
  unchecked: 'divider'
};

function AvailabilityBadge({ status }) {
  const s = STATUS_CHIP[status] || STATUS_CHIP.unchecked;
  return <Chip icon={s.icon} label={s.label} color={s.color} size="small" />;
}

function ConflictBox({ conflictSummary }) {
  if (!conflictSummary?.length) return null;
  return (
    <Box mt={1} sx={{ bgcolor: '#fff3e0', p: 1, borderRadius: 1 }}>
      <Typography variant="caption" fontWeight={700} color="warning.dark">CONFLICTS:</Typography>
      {conflictSummary.map((c, i) => (
        <Typography key={i} variant="caption" display="block" color="error">
          ❌ {c.name} ({c.role}) — {c.reason}
        </Typography>
      ))}
    </Box>
  );
}

const EMPTY_FORM = { proposedDate: '', startTime: '09:30', endTime: '18:30', location: '', notes: '' };

const EMPTY_SCENE = () => ({
  _key: Math.random().toString(36).slice(2),
  shootOrder: '', sceneNo: '', sceneName: '', startTime: '', hrs: '',
  artists: [], costume: '', specialProps: '', location: ''
});

const BREAK_ROW = (label = 'Lunch Break') => ({
  _key: Math.random().toString(36).slice(2),
  type: 'break', breakLabel: label,
  shootOrder: '', sceneNo: '', sceneName: '', startTime: '', hrs: '1',
  artists: [], costume: '', specialProps: '', location: ''
});

function CallsheetEditor({ option, castingList, onSaved, scenes, setScenes, contentTitle, contentId, channel, director, cinematographer, planStartTime }) {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState(false);

  const artists = Array.isArray(castingList)
    ? castingList
    : (() => { try { return JSON.parse(castingList || '[]'); } catch (_) { return []; } })();

  const updateRow = (key, field, value) =>
    setScenes(prev => prev.map(r => r._key === key ? { ...r, [field]: value } : r));

  // Shared: cascade start times from a given index onwards
  const cascadeFrom = (rows, fromIdx) => {
    const updated = [...rows];
    // Cascade from fromIdx: recalculate each row's startTime based on the previous row
    for (let i = fromIdx; i < updated.length; i++) {
      if (i === 0) continue; // first row keeps its own startTime
      const prev = updated[i - 1];
      if (!prev.startTime || !prev.hrs) break;
      const [hStr, mStr] = prev.startTime.split(':');
      const prevMins = (parseInt(hStr) || 0) * 60 + (parseInt(mStr) || 0);
      const totalMins = prevMins + Math.round((parseFloat(prev.hrs) || 0) * 60);
      const newH = Math.floor(totalMins / 60) % 24;
      const rawM = totalMins % 60;
      const snappedM = Math.round(rawM / 15) * 15;
      const carryH = snappedM >= 60 ? 1 : 0;
      const finalM = snappedM % 60;
      const finalH = (newH + carryH) % 24;
      updated[i] = { ...updated[i], startTime: `${String(finalH).padStart(2,'0')}:${String(finalM).padStart(2,'0')}` };
    }
    return updated;
  };
  // cascadeAfter is used when the row at idx already has its correct value — cascade only what follows
  const cascadeAfter = (rows, idx) => cascadeFrom(rows, idx + 1);

  // When start time changes on a row, cascade all following rows
  const updateStartTime = (key, value) => {
    setScenes(prev => {
      const updated = prev.map(r => r._key === key ? { ...r, startTime: value } : r);
      const idx = updated.findIndex(r => r._key === key);
      return cascadeAfter(updated, idx);
    });
  };

  // When hrs changes, cascade all following rows
  const updateHrs = (key, value) => {
    setScenes(prev => {
      const updated = prev.map(r => r._key === key ? { ...r, hrs: value } : r);
      const idx = updated.findIndex(r => r._key === key);
      return cascadeAfter(updated, idx);
    });
  };

  const addRow = () => setScenes(prev => {
    // If this is the first row and plan has a start time, seed it
    if (prev.length === 0 && planStartTime) {
      return [{ ...EMPTY_SCENE(), startTime: planStartTime }];
    }
    // Otherwise cascade: new row starts after the last row's time+hrs
    const last = prev[prev.length - 1];
    let newStart = '';
    if (last?.startTime && last?.hrs) {
      const [hStr, mStr] = last.startTime.split(':');
      const totalMins = (parseInt(hStr) || 0) * 60 + (parseInt(mStr) || 0) + Math.round((parseFloat(last.hrs) || 0) * 60);
      const newH = Math.floor(totalMins / 60) % 24;
      const rawM = totalMins % 60;
      const snappedM = Math.round(rawM / 15) * 15;
      const carryH = snappedM >= 60 ? 1 : 0;
      const finalM = snappedM % 60;
      const finalH = (newH + carryH) % 24;
      newStart = `${String(finalH).padStart(2,'0')}:${String(finalM).padStart(2,'0')}`;
    }
    return [...prev, { ...EMPTY_SCENE(), startTime: newStart }];
  });

  const addBreakRow = (label) => setScenes(prev => {
    const last = prev[prev.length - 1];
    let newStart = '';
    if (last?.startTime && last?.hrs) {
      const [hStr, mStr] = last.startTime.split(':');
      const totalMins = (parseInt(hStr) || 0) * 60 + (parseInt(mStr) || 0) + Math.round((parseFloat(last.hrs) || 0) * 60);
      const newH = Math.floor(totalMins / 60) % 24;
      const rawM = totalMins % 60;
      const snappedM = Math.round(rawM / 15) * 15;
      const carryH = snappedM >= 60 ? 1 : 0;
      const finalM = snappedM % 60;
      const finalH = (newH + carryH) % 24;
      newStart = `${String(finalH).padStart(2,'0')}:${String(finalM).padStart(2,'0')}`;
    }
    return [...prev, { ...BREAK_ROW(label), startTime: newStart }];
  });

  const copyRow = (key) => {
    setScenes(prev => {
      const idx = prev.findIndex(r => r._key === key);
      const src = prev[idx];
      // Calculate the start time for the copied row based on src's time + hrs
      let newStartTime = '';
      if (src.startTime && src.hrs) {
        const [hStr, mStr] = src.startTime.split(':');
        const totalMins = (parseInt(hStr) || 0) * 60 + (parseInt(mStr) || 0) + Math.round((parseFloat(src.hrs) || 0) * 60);
        const newH = Math.floor(totalMins / 60) % 24;
        const rawM = totalMins % 60;
        const snappedM = Math.round(rawM / 15) * 15;
        const carryH = snappedM >= 60 ? 1 : 0;
        const finalM = snappedM % 60;
        const finalH = (newH + carryH) % 24;
        newStartTime = `${String(finalH).padStart(2,'0')}:${String(finalM).padStart(2,'0')}`;
      }
      const copy = { ...src, _key: Math.random().toString(36).slice(2), shootOrder: '', startTime: newStartTime };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      // Cascade all rows after the inserted copy
      return cascadeAfter(next, idx + 1);
    });
  };

  const deleteRow = (key) =>
    setScenes(prev => prev.length > 1 ? prev.filter(r => r._key !== key) : prev);

  const moveRow = (key, dir) => setScenes(prev => {
    const idx = prev.findIndex(r => r._key === key);
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= prev.length) return prev;
    const next = [...prev];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    // Cascade from the earlier of the two positions so times stay consistent
    return cascadeFrom(next, Math.min(idx, newIdx));
  });
  const handleSave = async () => {
    setSaving(true); setSaveError(''); setSaved(false);
    // Mandatory: Scene No, Scene Name, Start Time, Artists for every row (skip break rows)
    const invalidRows = scenes.filter(r =>
      r.type !== 'break' && (!r.sceneNo?.trim() || !r.sceneName?.trim() || !r.startTime || !Array.isArray(r.artists) || r.artists.length === 0)
    );
    if (invalidRows.length > 0) {
      setSaveError(`Row${invalidRows.length > 1 ? 's' : ''} ${invalidRows.map((_, i) => scenes.indexOf(invalidRows[i]) + 1).join(', ')}: Scene No, Scene Name, Start Time and Artists are required.`);
      setSaving(false);
      return;
    }
    // Duplicate check: Scene No + Scene Name combination must be unique (skip break rows)
    const seen = new Set();
    const dupRows = [];
    scenes.forEach((r, i) => {
      if (r.type === 'break') return;
      const key = `${r.sceneNo?.trim().toLowerCase()}||${r.sceneName?.trim().toLowerCase()}`;
      if (seen.has(key)) dupRows.push(i + 1);
      else seen.add(key);
    });
    if (dupRows.length > 0) {
      setSaveError(`Duplicate Scene No + Name combination found at row${dupRows.length > 1 ? 's' : ''} ${dupRows.join(', ')}. Each scene must have a unique Scene No and Name combination.`);
      setSaving(false);
      return;
    }
    try {
      const clean = scenes.map(({ _key, ...r }) => r);
      await api.put(`/shoot-plans/options/${option.id}/callsheet`, { scenes: clean });
      setSaved(true);
      if (onSaved) onSaved(clean);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setSaveError(e.response?.data?.message || 'Failed to save callsheet');
    } finally { setSaving(false); }
  };

  const handleExportPDF = () => {
    const fmtHrs = (val) => {
      const total = parseFloat(val);
      if (!total) return '';
      const h = Math.floor(total);
      const m = Math.round((total - h) * 60);
      if (h && m) return `${h}h ${m}m`;
      if (h) return `${h}h`;
      return `${m}m`;
    };
    const rows = scenes.map((r, i) => {
      if (r.type === 'break') return `
      <tr>
        <td style="text-align:center;background:#fff3e0;color:#e65100;font-weight:700;letter-spacing:1px;" colspan="9">
          ☕ ${r.breakLabel || 'Break'} ${r.startTime ? '— ' + r.startTime : ''} ${r.hrs ? '(' + fmtHrs(r.hrs) + ')' : ''}
        </td>
      </tr>`;
      return `
      <tr>
        <td>${r.shootOrder || i + 1}</td>
        <td>${r.sceneNo || ''}</td>
        <td>${r.sceneName || ''}</td>
        <td>${r.startTime || ''}</td>
        <td>${fmtHrs(r.hrs)}</td>
        <td>${Array.isArray(r.artists) ? r.artists.join(', ') : r.artists || ''}</td>
        <td>${r.costume || ''}</td>
        <td>${r.specialProps || ''}</td>
        <td>${r.location || ''}</td>
      </tr>`;
    }).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Shoot Scene Plan for ${contentTitle || ''}</title>
<style>
  body{font-family:Arial,sans-serif;margin:24px;font-size:12px;}
  .header{text-align:center;margin-bottom:14px;}
  .header h2{font-size:18px;margin:0 0 2px;letter-spacing:1px;}
  .header h3{font-size:13px;color:#333;margin:0 0 8px;}
  .meta{display:flex;justify-content:center;gap:32px;font-size:11px;color:#444;margin-bottom:12px;border-top:1px solid #ccc;border-bottom:1px solid #ccc;padding:6px 0;}
  .meta span b{color:#1a237e;}
  table{width:100%;border-collapse:collapse;}
  th,td{border:1px solid #999;padding:5px 7px;text-align:left;vertical-align:top;}
  th{background:#e8eaf6;font-weight:700;color:#1a237e;}
  tr:nth-child(even) td{background:#f9f9f9;}
  @media print{body{margin:12px;}}
</style></head><body>
<div class="header">
  <h2>SHOOT SCENE PLAN (CALLSHEET)</h2>
  <h3>${option.label} &nbsp;·&nbsp; ${option.proposedDate}</h3>
</div>
<div class="meta">
  <span><b>Content:</b> ${contentTitle || '—'}</span>
  <span><b>ID:</b> ${contentId || '—'}</span>
  <span><b>Channel:</b> ${channel || '—'}</span>
  <span><b>Director:</b> ${director || '—'}</span>
  ${cinematographer ? `<span><b>Cinematographer:</b> ${cinematographer}</span>` : ''}
  <span><b>Time:</b> ${option.startTime || ''} – ${option.endTime || ''}</span>
  ${option.location ? `<span><b>Location:</b> ${option.location}</span>` : ''}
</div>
<table>
  <thead><tr>
    <th>Shoot Order</th><th>Scene No</th><th>Scene Name</th>
    <th>Start Time</th><th>Hrs</th><th>Artists</th><th>Costume</th><th>Special Props</th><th>Location</th>
  </tr></thead>
  <tbody>${rows}</tbody>
</table>
</body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html); w.document.close(); w.focus();
    // Set document title so browser uses it as the PDF filename
    w.document.title = `Shoot Scene Plan for ${contentTitle || option.label}`;
    setTimeout(() => w.print(), 400);
  };

  const COL_WIDTHS = ['36px','44px','140px','148px','62px','170px','140px','110px','110px','68px'];

  return (
    <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
      {/* Header bar */}
      <Box sx={{ bgcolor: '#1a237e', px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#fff' }}>
            Scene Planning — {option.label}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            {option.proposedDate}
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <Button size="small" startIcon={<AddIcon />} onClick={addRow}
            sx={{ borderColor: 'rgba(255,255,255,0.4)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
            Add Row
          </Button>
          <Button size="small" startIcon={<AddIcon />} onClick={() => addBreakRow('Lunch Break')}
            sx={{ borderColor: 'rgba(255,255,255,0.4)', color: '#ffcc80', border: '1px solid rgba(255,255,255,0.3)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
            Lunch Break
          </Button>
          <Button size="small" startIcon={<AddIcon />} onClick={() => addBreakRow('Break')}
            sx={{ borderColor: 'rgba(255,255,255,0.4)', color: '#ffcc80', border: '1px solid rgba(255,255,255,0.3)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
            Break
          </Button>
          <Button size="small" variant="outlined"
            startIcon={<PdfIcon />} onClick={handleExportPDF}
            sx={{ borderColor: 'rgba(255,255,255,0.6)', color: '#fff', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
            PDF
          </Button>
          <Button size="small" variant="contained"
            startIcon={saving ? <CircularProgress size={14} sx={{ color: '#1a237e' }} /> : <SaveIcon />}
            onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#fff', color: '#1a237e', fontWeight: 700, '&:hover': { bgcolor: '#e8eaf6' }, '&:disabled': { bgcolor: 'rgba(255,255,255,0.4)' } }}>
            {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save'}
          </Button>
        </Box>
      </Box>

      {saveError && <Alert severity="error" sx={{ mx: 2, mt: 1 }}>{saveError}</Alert>}
      {saved && <Alert severity="success" sx={{ mx: 2, mt: 1 }}>Callsheet saved successfully.</Alert>}

      <TableContainer sx={{ overflowX: 'auto', mt: 1 }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              {['#','Scene No','Scene Name','Start Time','Hrs','Artists','Costume','Special Props','Location',''].map((h, i) => (
                <TableCell key={h + i} sx={{
                  fontWeight: 700, fontSize: '0.72rem', py: 1, px: 1,
                  width: COL_WIDTHS[i], whiteSpace: 'nowrap',
                  bgcolor: '#e8eaf6', color: '#1a237e', borderBottom: '2px solid #9fa8da'
                }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {scenes.map((row, idx) => {
              // Break row — compact banner with move arrows
              if (row.type === 'break') return (
                <TableRow key={row._key} sx={{ bgcolor: '#fff8f0', borderTop: '2px dashed #ffcc80', borderBottom: '2px dashed #ffcc80' }}>
                  {/* # */}
                  <TableCell sx={{ p: 0.5, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.72rem', color: '#aaa' }}>{idx + 1}</Typography>
                  </TableCell>
                  {/* Banner content spanning all middle cols */}
                  <TableCell colSpan={8} sx={{ py: 0.75, px: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <CoffeeIcon sx={{ color: '#e65100', fontSize: '1.1rem' }} />
                      <Select size="small" value={row.breakLabel || 'Lunch Break'}
                        onChange={e => updateRow(row._key, 'breakLabel', e.target.value)}
                        variant="standard" disableUnderline
                        sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#e65100', minWidth: 120, '.MuiSelect-select': { py: 0 } }}>
                        <MenuItem value="Lunch Break">Lunch Break</MenuItem>
                        <MenuItem value="Break">Break</MenuItem>
                        <MenuItem value="Tea Break">Tea Break</MenuItem>
                        <MenuItem value="Dinner Break">Dinner Break</MenuItem>
                      </Select>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mx: 0.5 }}>|</Typography>
                      {/* Start time */}
                      {(() => {
                        const [hStr = '', mStr = ''] = (row.startTime || '').split(':');
                        const h24 = parseInt(hStr) || 0;
                        const isPM = h24 >= 12;
                        const h12 = h24 % 12 || 12;
                        const mins = mStr || '00';
                        return <>
                          <Select size="small" value={String(h12)}
                            onChange={e => { const h = parseInt(e.target.value); const h24v = isPM ? (h === 12 ? 12 : h + 12) : (h === 12 ? 0 : h); updateStartTime(row._key, `${String(h24v).padStart(2,'0')}:${mins}`); }}
                            sx={{ fontSize: '0.75rem', '.MuiSelect-select': { py: '3px', px: '4px' } }}>
                            {[12,1,2,3,4,5,6,7,8,9,10,11].map(h => <MenuItem key={h} value={String(h)} dense sx={{ fontSize: '0.8rem' }}>{h}</MenuItem>)}
                          </Select>
                          <Typography sx={{ fontSize: '0.8rem', color: '#666' }}>:</Typography>
                          <Select size="small" value={mins}
                            onChange={e => { const h24v = parseInt(hStr) || 0; updateStartTime(row._key, `${String(h24v).padStart(2,'0')}:${e.target.value}`); }}
                            sx={{ fontSize: '0.75rem', '.MuiSelect-select': { py: '3px', px: '4px' } }}>
                            {['00','15','30','45'].map(m => <MenuItem key={m} value={m} dense sx={{ fontSize: '0.8rem' }}>{m}</MenuItem>)}
                          </Select>
                          <Select size="small" value={isPM ? 'PM' : 'AM'}
                            onChange={e => { const am = e.target.value === 'AM'; const h24v = am ? (h12 === 12 ? 0 : h12) : (h12 === 12 ? 12 : h12 + 12); updateStartTime(row._key, `${String(h24v).padStart(2,'0')}:${mins}`); }}
                            sx={{ fontSize: '0.75rem', '.MuiSelect-select': { py: '3px', px: '4px' } }}>
                            <MenuItem value="AM" dense sx={{ fontSize: '0.8rem' }}>AM</MenuItem>
                            <MenuItem value="PM" dense sx={{ fontSize: '0.8rem' }}>PM</MenuItem>
                          </Select>
                        </>;
                      })()}
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mx: 0.5 }}>|</Typography>
                      <Select size="small" value={row.hrs || '0.25'}
                        onChange={e => updateHrs(row._key, e.target.value)}
                        sx={{ fontSize: '0.75rem', minWidth: 65, '.MuiSelect-select': { py: '3px', px: '4px' } }}>
                        {[['0.25','15m'],['0.5','30m'],['0.75','45m'],['1','1h'],['1.5','1h 30m'],['2','2h']].map(([val, label]) => <MenuItem key={val} value={val} dense sx={{ fontSize: '0.8rem' }}>{label}</MenuItem>)}
                      </Select>
                    </Box>
                  </TableCell>
                  {/* Actions */}
                  <TableCell sx={{ p: 0.5, whiteSpace: 'nowrap' }}>
                    <Tooltip title="Move up">
                      <span><IconButton size="small" onClick={() => moveRow(row._key, -1)} disabled={idx === 0}><MoveUpIcon sx={{ fontSize: '0.9rem' }} /></IconButton></span>
                    </Tooltip>
                    <Tooltip title="Move down">
                      <span><IconButton size="small" onClick={() => moveRow(row._key, 1)} disabled={idx === scenes.length - 1}><MoveDownIcon sx={{ fontSize: '0.9rem' }} /></IconButton></span>
                    </Tooltip>
                    <Tooltip title="Delete break">
                      <IconButton size="small" color="error" onClick={() => deleteRow(row._key)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
              return (
              <TableRow key={row._key} sx={{
                bgcolor: idx % 2 === 0 ? '#fff' : '#f5f5f5',
                '&:hover': { bgcolor: '#e8eaf620' },
                borderBottom: '1px solid #e0e0e0'
              }}>
                {/* Shoot Order */}
                <TableCell sx={{ p: 0.5 }}>
                  <TextField size="small" value={row.shootOrder} placeholder={String(idx + 1)}
                    onChange={e => updateRow(row._key, 'shootOrder', e.target.value)}
                    inputProps={{ style: { padding: '4px 2px', width: 22, textAlign: 'center' }, maxLength: 2 }} />
                </TableCell>
                {/* Scene No */}
                <TableCell sx={{ p: 0.5 }}>
                  <TextField size="small" value={row.sceneNo}
                    onChange={e => updateRow(row._key, 'sceneNo', e.target.value)}
                    inputProps={{ style: { padding: '4px 2px', width: 30 }, maxLength: 2 }}
                    error={!row.sceneNo?.trim()} />
                </TableCell>
                {/* Scene Name */}
                <TableCell sx={{ p: 0.5 }}>
                  <TextField size="small" value={row.sceneName} fullWidth
                    onChange={e => updateRow(row._key, 'sceneName', e.target.value)}
                    error={!row.sceneName?.trim()}
                    inputProps={{ style: { padding: '4px 6px' } }} />
                </TableCell>
                {/* Start Time — Hour + Minute selects */}
                <TableCell sx={{ p: 0.5 }}>
                  {(() => {
                    const [hStr = '', mStr = ''] = (row.startTime || '').split(':');
                    const h24 = parseInt(hStr) || 0;
                    const isPM = h24 >= 12;
                    const h12 = h24 % 12 || 12;
                    const rawM = parseInt(mStr) || 0;
                    // Snap to nearest valid minute option
                    const validMins = [0, 15, 30, 45];
                    const snapped = validMins.reduce((a, b) => Math.abs(b - rawM) < Math.abs(a - rawM) ? b : a, 0);
                    const mins = String(snapped).padStart(2, '0');
                    const setTime = (newH12, newMins, newIsPM) => {
                      const h24v = (newH12 % 12) + (newIsPM ? 12 : 0);
                      updateStartTime(row._key, `${String(h24v).padStart(2,'0')}:${newMins}`);
                    };
                    return (
                      <Box display="flex" gap={0.25} alignItems="center">
                        <Select size="small" value={h12}
                          onChange={e => setTime(e.target.value, mins, isPM)}
                          error={!row.startTime}
                          sx={{ fontSize: '0.75rem', '& .MuiSelect-select': { py: '3px', px: '4px' }, minWidth: 42 }}>
                          {[12,1,2,3,4,5,6,7,8,9,10,11].map(h => <MenuItem key={h} value={h} dense sx={{ fontSize: '0.8rem' }}>{h}</MenuItem>)}
                        </Select>
                        <Select size="small" value={mins}
                          onChange={e => setTime(h12, e.target.value, isPM)}
                          sx={{ fontSize: '0.75rem', '& .MuiSelect-select': { py: '3px', px: '4px' }, minWidth: 42 }}>
                          {['00','15','30','45'].map(m => <MenuItem key={m} value={m} dense sx={{ fontSize: '0.8rem' }}>{m}</MenuItem>)}
                        </Select>
                        <Select size="small" value={isPM ? 'PM' : 'AM'}
                          onChange={e => setTime(h12, mins, e.target.value === 'PM')}
                          sx={{ fontSize: '0.75rem', '& .MuiSelect-select': { py: '3px', px: '3px' }, minWidth: 46 }}>
                          <MenuItem value="AM" dense sx={{ fontSize: '0.8rem' }}>AM</MenuItem>
                          <MenuItem value="PM" dense sx={{ fontSize: '0.8rem' }}>PM</MenuItem>
                        </Select>
                      </Box>
                    );
                  })()}
                </TableCell>
                {/* Hrs — auto-cascades start times */}
                <TableCell sx={{ p: 0.5 }}>
                  <Select size="small" value={row.hrs || ''}
                    onChange={e => updateHrs(row._key, e.target.value)}
                    displayEmpty
                    sx={{ fontSize: '0.75rem', '& .MuiSelect-select': { py: '3px', px: '4px' }, minWidth: 54 }}>
                    <MenuItem value="" dense sx={{ fontSize: '0.8rem', color: '#aaa' }}>—</MenuItem>
                    {['0.25','0.5','0.75','1','1.5','2','2.5','3','3.5','4','4.5','5','6'].map(h => (
                      <MenuItem key={h} value={h} dense sx={{ fontSize: '0.8rem' }}>
                        {h === '0.25' ? '15m' : h === '0.5' ? '30m' : h === '0.75' ? '45m' : `${h}h`}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                {/* Artists — multi-select from castingList */}
                <TableCell sx={{ p: 0.5 }}>
                  <FormControl size="small" sx={{ minWidth: 160 }} error={!Array.isArray(row.artists) || row.artists.length === 0}>
                    <Select
                      multiple
                      value={Array.isArray(row.artists) ? row.artists : []}
                      onChange={e => updateRow(row._key, 'artists', e.target.value)}
                      input={<OutlinedInput sx={{ fontSize: '0.75rem' }} />}
                      renderValue={sel => sel.join(', ')}
                      sx={{ fontSize: '0.75rem', '& .MuiSelect-select': { py: '4px', px: '6px' } }}
                    >
                      {artists.map(name => (
                        <MenuItem key={name} value={name} dense>
                          <Checkbox size="small" checked={Array.isArray(row.artists) && row.artists.includes(name)} sx={{ p: 0.5 }} />
                          <ListItemText primary={name} primaryTypographyProps={{ fontSize: '0.8rem' }} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                {/* Costume */}
                <TableCell sx={{ p: 0.5 }}>
                  <TextField size="small" value={row.costume} fullWidth multiline minRows={2} maxRows={4}
                    onChange={e => updateRow(row._key, 'costume', e.target.value)}
                    inputProps={{ style: { padding: '4px 6px', fontSize: '0.8rem' } }} />
                </TableCell>
                {/* Special Props */}
                <TableCell sx={{ p: 0.5 }}>
                  <TextField size="small" value={row.specialProps} fullWidth multiline minRows={2} maxRows={4}
                    onChange={e => updateRow(row._key, 'specialProps', e.target.value)}
                    inputProps={{ style: { padding: '4px 6px', fontSize: '0.8rem' } }} />
                </TableCell>
                {/* Location */}
                <TableCell sx={{ p: 0.5 }}>
                  <TextField size="small" value={row.location} fullWidth multiline minRows={2} maxRows={4}
                    onChange={e => updateRow(row._key, 'location', e.target.value)}
                    inputProps={{ style: { padding: '4px 6px', fontSize: '0.8rem' } }} />
                </TableCell>
                {/* Actions */}
                <TableCell sx={{ p: 0.5, whiteSpace: 'nowrap' }}>
                  <Tooltip title="Move up">
                    <span><IconButton size="small" onClick={() => moveRow(row._key, -1)} disabled={idx === 0}><MoveUpIcon sx={{ fontSize: '0.9rem' }} /></IconButton></span>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <span><IconButton size="small" onClick={() => moveRow(row._key, 1)} disabled={idx === scenes.length - 1}><MoveDownIcon sx={{ fontSize: '0.9rem' }} /></IconButton></span>
                  </Tooltip>
                  <Tooltip title="Copy row">
                    <IconButton size="small" onClick={() => copyRow(row._key)}><CopyIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Delete row">
                    <IconButton size="small" color="error" onClick={() => deleteRow(row._key)}><DeleteIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
              ); })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}



function ShootPlanner() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [proposing, setProposing] = useState(false); // contentId being proposed, or 'all'
  const [selected, setSelected] = useState(null);
  const [proposalResult, setProposalResult] = useState(null);
  const [error, setError] = useState('');

  // Add / Edit dialog state
  const [dialog, setDialog] = useState({ open: false, mode: 'add', option: null });
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState('');
  // Live preview of availability while editing/adding
  const [preview, setPreview] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [previewing, setPreviewing] = useState(false);

  const [typeFilter, setTypeFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [leftTab, setLeftTab] = useState(0); // 0=Awaiting, 1=Confirmed, 2=Completed
  const [detailTab, setDetailTab] = useState(0); // 0=Plan Options, 1=Callsheet
  const [maximized, setMaximized] = useState(false);
  const [callsheetScenes, setCallsheetScenes] = useState(null); // lifted state — survives expand/minimize
  const [callsheetOptionId, setCallsheetOptionId] = useState(null); // tracks which option the scenes belong to
  const rightPanelRef = useRef(null);

  const fetchContents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/shoot-plans/pending-content');
      const list = res.data.data.contents || [];
      setContents(list);
      // Refresh selected item — keep same tab, only reset tab if selection changes
      setSelected(prev => {
        if (!prev) return null;
        const updated = list.find(c => c.id === prev.id);
        if (!updated) { setDetailTab(0); return null; }
        return updated;
      });
    } catch (err) {
      console.error('fetchContents failed:', err?.response?.status, err?.response?.data || err?.message);
      setError('Failed to load content: ' + (err?.response?.data?.message || err?.message || 'Unknown error'));
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchContents(); }, [fetchContents]);

  // Minimize on Escape key when maximized — Box overlay doesn't handle this automatically
  useEffect(() => {
    if (!maximized) return;
    const handleKey = (e) => { if (e.key === 'Escape') setMaximized(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [maximized]);

  // Auto-dismiss alerts after 6 seconds
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(''), 6000);
    return () => clearTimeout(t);
  }, [error]);

  useEffect(() => {
    if (!proposalResult) return;
    const t = setTimeout(() => setProposalResult(null), 6000);
    return () => clearTimeout(t);
  }, [proposalResult]);

  // Sync callsheet scenes to lifted state when active option changes
  useEffect(() => {
    if (!selected?.shootPlan) return;
    const confirmedId = selected.shootPlan.confirmedOptionId;
    const confirmedOpt = selected.shootPlan.options?.find(
      o => o.id === confirmedId || String(o.id) === String(confirmedId)
    );
    const activeOption = confirmedOpt || selected.shootPlan.options?.[selected.shootPlan.options.length - 1];
    if (!activeOption) return;
    // Only reset scenes when switching to a different option (not on every re-render)
    if (callsheetOptionId === activeOption.id) return;
    const s = activeOption.callsheetScenes || [];
    const planStart = activeOption.startTime || '';
    setCallsheetScenes(s.length
      ? s.map(r => ({ ...r, _key: Math.random().toString(36).slice(2) }))
      : [{ ...EMPTY_SCENE(), startTime: planStart }]);
    setCallsheetOptionId(activeOption.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, selected?.shootPlan?.confirmedOptionId]);

  useEffect(() => {
    if (selected && rightPanelRef.current) {
      rightPanelRef.current.scrollTop = 0;
    }
  }, [selected]);

  // Live availability preview whenever date changes in the dialog
  useEffect(() => {
    if (!form.proposedDate || !selected) { setPreview(null); return; }
    const timeout = setTimeout(async () => {
      setPreviewing(true);
      try {
        // eslint-disable-next-line no-unused-vars
        const planId = selected.shootPlan?.id;
        // eslint-disable-next-line no-unused-vars
        const optionId = dialog.mode === 'edit' ? dialog.option?.id : null;
        // Use addOption / updateOption endpoints' internal check by doing a dry-run
        // Instead, call a dedicated check if available; otherwise use availability directly
        // We call the addOption endpoint in preview mode — we pass a special flag, OR
        // we replicate by calling the pending-content re-check. Simplest: just POST the
        // option and immediately delete it? No — instead call a check endpoint.
        // Actual approach: send to a lightweight check route if we add one, or just
        // attempt the save and show the result. For now: re-fetch content with the date
        // context isn't ideal, so we'll call the backend availability inline via add (dry-run).
        // Simplest implementation: POST option, capture conflicts, then delete immediately.
        // Better UX: call a dedicated /check-availability route.
        // Since we don't have one yet, we'll call the add endpoint tentatively —
        // actually the cleanest approach for now is to check on actual save.
        // For true live preview, add a check route. For now leave preview null.
        setPreview(null);
      } catch (_) {}
      finally { setPreviewing(false); }
    }, 600);
    return () => clearTimeout(timeout);
  }, [form.proposedDate, selected, dialog.mode, dialog.option]);

  const handleAutoPropose = async (contentId = null) => {
    setError(''); setProposalResult(null);

    if (contentId && selected?.shootPlan) {
      const optionCount = selected.shootPlan.options?.length || 0;
      const isConfirmed = selected.shootPlan.status === 'confirmed';

      // Already confirmed — block, show error
      if (isConfirmed) {
        const confirmDate = selected.shootPlan.options?.find(
          o => o.id === selected.shootPlan.confirmedOptionId
        )?.proposedDate || '';
        const dateStr = confirmDate ? new Date(confirmDate + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'a scheduled date';
        setError(`This content already has an Active Shoot Plan confirmed for ${dateStr}. To reschedule, use the ✏️ Edit button on the existing plan. Delete the current plan first if you need a new one.`);
        setProposing(false);
        return;
      }

      // Already has 2 unconfirmed options — block and tell user to delete one
      if (!isConfirmed && optionCount >= 2) {
        setError('This content already has 2 plan options. Please delete one before auto-proposing again.');
        return;
      }
    }
    setProposing(contentId || 'all');
    setProposalResult(null);
    try {
      const body = contentId ? { contentId } : {};
      const res = await api.post('/shoot-plans/auto-propose', body);
      setProposalResult(res.data.message);
      await fetchContents();
      if (contentId) setLeftTab(0);
    } catch (e) { setError(e.response?.data?.message || 'Auto-propose failed'); }
    finally { setProposing(false); }
  };

  const handleConfirm = async (planId, optionId, option) => {
    setError(''); setProposalResult(null);
    const isOverloaded = option?.availabilityStatus === 'overloaded';
    const limitWarning = option?.conflictSummary?.find(c => c.username === '__limit__')?.reason;
    const msg = isOverloaded
      ? `⚠️ ${limitWarning}\n\nConfirm this plan anyway?`
      : 'Confirm this plan? The content shoot date will be updated automatically.';
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/shoot-plans/${planId}/confirm/${optionId}`);
      await fetchContents();
    } catch (e) {
      const msg = e.response?.data?.message || 'Failed to confirm plan';
      // 409 = day limit exceeded — show as a prominent error
      setError(msg);
    }
  };

  const handleDeleteOption = async (optionId) => {
    setError(''); setProposalResult(null);
    if (!window.confirm('Delete this plan option?')) return;
    try {
      await api.delete(`/shoot-plans/options/${optionId}`);
      await fetchContents();
    } catch (e) { setError(e.response?.data?.message || 'Failed to delete option'); }
  };

  const handleResetPlan = async () => {
    setError(''); setProposalResult(null);
    if (!selected?.shootPlan) return;
    const hasActive = !!selected.shootPlan.confirmedOptionId;
    const confirmedOpt = selected.shootPlan.options?.find(o => o.id === selected.shootPlan.confirmedOptionId);

    if (hasActive) {
      const confirmDate = confirmedOpt?.proposedDate || '';
      const dateStr = confirmDate ? new Date(confirmDate + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'a scheduled date';
      const hasCallsheet = confirmedOpt?.callsheetScenes?.length > 0;
      const extra = hasCallsheet ? ' The callsheet data will also be lost.' : '';
      setError(`This content has an Active Shoot Plan confirmed for ${dateStr}.${extra} To change the date or time, use the ✏️ Edit button on the existing plan instead. To reset, first delete the active plan.`);
      return;
    }

    if (!window.confirm('Remove ALL plan options and reset to draft? You can add options manually after this.')) return;
    try {
      await api.delete(`/shoot-plans/${selected.shootPlan.id}/reset`);
      await fetchContents();
    } catch (e) { setError(e.response?.data?.message || 'Failed to reset plan'); }
  };

  const handleShootToday = async () => {
    setError(''); setProposalResult(null);
    if (!selected) return;
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    if (selected?.shootPlan?.status === 'confirmed') {
      const confirmDate = selected.shootPlan.options?.find(
        o => o.id === selected.shootPlan.confirmedOptionId
      )?.proposedDate || '';
      const dateStr = confirmDate ? new Date(confirmDate + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'a scheduled date';
      setError(`This content already has an Active Shoot Plan confirmed for ${dateStr}. To reschedule to today, use the ✏️ Edit button on the existing plan and change the date. Delete the current plan first if you need a new one.`);
      return;
    }
    if (!window.confirm(`Create and confirm a shoot plan for today (${todayStr})?`)) return;
    try {
      let planId = selected.shootPlan?.id;

      // Create plan if none exists
      if (!planId) {
        const created = await api.post('/shoot-plans', { contentId: selected.id });
        planId = created.data.data.plan.id;
      }

      // Check if today's option already exists
      const existing = selected.shootPlan?.options?.find(o => o.proposedDate === todayStr);
      let optionId = existing?.id;

      if (!optionId) {
        const opt = await api.post(`/shoot-plans/${planId}/options`, {
          proposedDate: todayStr, startTime: '09:30', endTime: '18:30'
        });
        optionId = opt.data.data.option.id;
      }

      await api.post(`/shoot-plans/${planId}/confirm/${optionId}`);
      await fetchContents();
    } catch (e) { setError(e.response?.data?.message || 'Failed to schedule shoot today'); }
  };

  const openAdd = () => {
    if (selected?.shootPlan?.status === 'confirmed') {
      const confirmDate = selected.shootPlan.options?.find(
        o => o.id === selected.shootPlan.confirmedOptionId
      )?.proposedDate || '';
      const dateStr = confirmDate ? new Date(confirmDate + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'a scheduled date';
      setError(`This content already has an Active Shoot Plan confirmed for ${dateStr}. To change the date or time, use the ✏️ Edit button on the existing plan. If you need a new plan, delete the current one first.`);
      return;
    }
    setForm(EMPTY_FORM);
    setPreview(null);
    setDialogError('');
    setDialog({ open: true, mode: 'add', option: null });
  };

  const openEdit = (opt) => {
    setForm({
      proposedDate: opt.proposedDate || '',
      startTime: opt.startTime || '09:30',
      endTime: opt.endTime || '18:30',
      location: opt.location || '',
      notes: opt.notes || ''
    });
    setPreview(null);
    setDialogError('');
    setDialog({ open: true, mode: 'edit', option: opt });
  };

  const handleSave = async () => {
    if (!form.proposedDate) return;
    if (dialog.mode === 'edit' && !form.notes.trim()) {
      setDialogError('Please provide a reason for the schedule change.');
      return;
    }
    setSaving(true);
    setDialogError('');
    try {
      if (dialog.mode === 'add') {
        await api.post(`/shoot-plans/${selected.shootPlan.id}/options`, form);
      } else {
        await api.put(`/shoot-plans/options/${dialog.option.id}`, form);
      }
      setDialog({ open: false, mode: 'add', option: null });
      await fetchContents();
    } catch (e) {
      setDialogError(e.response?.data?.message || 'Failed to save option');
    } finally { setSaving(false); }
  };

  const handleCreatePlan = async (content) => {
    try {
      await api.post('/shoot-plans', { contentId: content.id });
      await fetchContents();
    } catch (e) { setError(e.response?.data?.message || 'Failed to create plan'); }
  };

  const confirmed  = contents.filter(c => c.shootPlan?.status === 'confirmed' && c.status !== 'Shoot Completed');
  const completed  = contents.filter(c => c.status === 'Shoot Completed');
  const pending    = contents.filter(c => c.shootPlan?.status !== 'confirmed' && c.status !== 'Shoot Completed');

  const applyFilters = (list) => list.filter(c => {
    if (typeFilter && c.contentType !== typeFilter) return false;
    if (searchFilter && !c.contentTitle?.toLowerCase().includes(searchFilter.toLowerCase())) return false;
    return true;
  });

  // Sort options: active (confirmed) option first, then rest
  const sortedOptions = (plan) => {
    if (!plan?.options?.length) return [];
    const confirmedId = plan.confirmedOptionId;
    return [...plan.options].sort((a, b) => {
      if (a.id === confirmedId) return -1;
      if (b.id === confirmedId) return 1;
      return 0;
    });
  };

  // Shared plan detail body — used by both desktop panel and mobile dialog
  const renderPlanDetail = () => {
    if (!selected) return null;
    const isCompleted = selected.status === 'Shoot Completed';
    return (
    <Box>
      {/* Content header */}
      <Box display="flex" justifyContent="space-between" alignItems="start" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h6" fontWeight={700}>{selected.contentTitle}</Typography>
          <Typography variant="caption" color="primary">{selected.contentId}</Typography>
          <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
            <Chip label={selected.channel} size="small" variant="outlined" />
            <Chip label={selected.contentType} size="small" variant="outlined" />
            <Chip label={selected.status} size="small" color="warning" />
          </Box>
        </Box>
        {selected.shootPlan && !isCompleted && (
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            <Button size="small" variant="contained" color="secondary"
              startIcon={proposing === selected.id ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <AutoIcon />}
              disabled={!!proposing} onClick={() => handleAutoPropose(selected.id)}>
              {proposing === selected.id ? 'Proposing…' : 'Auto-Propose'}
            </Button>
            <Button size="small" startIcon={<AddIcon />} onClick={openAdd} variant="outlined">Add</Button>
            <Button size="small" variant="contained" startIcon={<ShootTodayIcon />}
              onClick={handleShootToday} sx={{ bgcolor: '#2E7D32', '&:hover': { bgcolor: '#1B5E20' } }}>
              Shoot Today
            </Button>
            <Button size="small" startIcon={<ResetIcon />} color="error" variant="outlined" onClick={handleResetPlan}>
              Reset
            </Button>
            <Tooltip title="Expand to full screen">
              <Button size="small" variant="contained"
                startIcon={<FullscreenIcon />}
                onClick={() => setMaximized(true)}
                sx={{ bgcolor: '#455a64', '&:hover': { bgcolor: '#37474f' }, display: maximized ? 'none' : 'inline-flex' }}>
                Expand
              </Button>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Crew summary */}
      <Box sx={{ bgcolor: '#f5f5f5', p: 1.5, borderRadius: 1, mb: 2 }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary">CREW (from content)</Typography>
        <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
          {selected.director && <Chip label={`🎬 ${selected.director}`} size="small" color="primary" variant="outlined" />}
          {selected.cinematographer && <Chip label={`🎥 ${selected.cinematographer}`} size="small" variant="outlined" />}
          {selected.castingList && (Array.isArray(selected.castingList)
            ? selected.castingList : JSON.parse(selected.castingList || '[]')
          ).map(name => <Chip key={name} label={`🎭 ${name}`} size="small" variant="outlined" />)}
        </Box>
      </Box>

      {/* Plan Options / Callsheet tabs — only show when plan exists */}
      {selected.shootPlan && (() => {
        const confirmedId = selected.shootPlan.confirmedOptionId;
        const confirmedOpt = selected.shootPlan.options?.find(
          o => o.id === confirmedId || String(o.id) === String(confirmedId)
        );
        const isLongVideo = selected.contentType === 'Long Video';
        const isPlanConfirmed = selected.shootPlan.status === 'confirmed';
        const callsheetDisabledReason = (!confirmedOpt && !isPlanConfirmed)
          ? 'Confirm a plan first to add callsheet'
          : !isLongVideo
          ? 'Callsheet is only required for Long Videos'
          : null;
        const activeOption = confirmedOpt || selected.shootPlan.options?.[selected.shootPlan.options.length - 1];

        return (
          <>
            {/* Tab bar */}
            <Box sx={{ display: 'flex', borderBottom: '2px solid #e0e0e0', mb: 2, mt: 1 }}>
              {[
                { label: '📋 Plan Options', idx: 0, disabled: false },
                { label: '🎬 Callsheet (Scene Planning)', idx: 1, disabled: !!callsheetDisabledReason, tooltip: callsheetDisabledReason }
              ].map(({ label, idx, disabled, tooltip }) => {
                const active = detailTab === idx;
                const btn = (
                  <Box
                    key={idx}
                    onClick={() => !disabled && setDetailTab(idx)}
                    sx={{
                      px: 3, py: 1.2,
                      fontWeight: active ? 700 : 500,
                      fontSize: '0.85rem',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      color: disabled ? 'text.disabled' : active ? '#1a237e' : 'text.secondary',
                      borderBottom: active ? '3px solid #1a237e' : '3px solid transparent',
                      mb: '-2px',
                      bgcolor: active ? '#e8eaf620' : 'transparent',
                      borderRadius: '6px 6px 0 0',
                      userSelect: 'none',
                      transition: 'all 0.15s',
                      '&:hover': disabled ? {} : { color: '#1a237e', bgcolor: '#e8eaf640' },
                    }}
                  >
                    {label}
                  </Box>
                );
                return tooltip ? <Tooltip key={idx} title={tooltip} placement="top">{btn}</Tooltip> : btn;
              })}
            </Box>

            {detailTab === 0 && <Box />}

            {detailTab === 1 && (confirmedOpt || isPlanConfirmed) && isLongVideo && activeOption && callsheetScenes && (
              <CallsheetEditor
                key={activeOption.id}
                option={activeOption}
                scenes={callsheetScenes}
                setScenes={setCallsheetScenes}
                castingList={selected.castingList}
                contentTitle={selected.contentTitle}
                contentId={selected.contentId}
                channel={selected.channel}
                director={selected.director}
                cinematographer={selected.cinematographer}
                planStartTime={activeOption.startTime || ''}
                onSaved={() => fetchContents()}
              />
            )}
          </>
        );
      })()}

      {/* No plan yet */}
      {!selected.shootPlan ? (
        isCompleted ? (
          <Alert severity="success" icon={false}>
            Shoot completed on {formatDate(selected.shootDate) || '—'}. No further planning needed.
          </Alert>
        ) : (
        <Box textAlign="center" py={4}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            No shoot plan yet. Auto-Propose dates or create one manually.
          </Typography>
          <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
            <Button variant="contained" color="secondary"
              startIcon={proposing === selected.id ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <AutoIcon />}
              disabled={!!proposing} onClick={() => handleAutoPropose(selected.id)}>
              {proposing === selected.id ? 'Proposing…' : 'Auto-Propose'}
            </Button>
            <Button variant="contained" startIcon={<ShootTodayIcon />}
              onClick={handleShootToday} sx={{ bgcolor: '#2E7D32', '&:hover': { bgcolor: '#1B5E20' } }}>
              Shoot Today
            </Button>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => handleCreatePlan(selected)}>
              Create Plan Manually
            </Button>
          </Box>
        </Box>
        )
      ) : (
        detailTab === 0 && <Stack spacing={2}>
          {(selected.shootPlan.options || []).length === 0 ? (
            <Alert severity="info">No options yet. Click "Add Option" to add a shoot date.</Alert>
          ) : (
            sortedOptions(selected.shootPlan).map((opt) => {
              const isActive = selected.shootPlan.confirmedOptionId === opt.id;
              return (
                <Box key={opt.id} sx={{
                  border: '2px solid',
                  borderColor: isActive ? 'success.main' : BORDER_COLOR[opt.availabilityStatus] || 'divider',
                  borderRadius: 1, p: 2,
                  bgcolor: isActive ? '#f1f8e9' : 'background.paper'
                }}>
                  {isActive && (
                    <Chip label="✓ Active Shoot Plan" color="success" size="small"
                      sx={{ fontWeight: 700 }} />
                  )}
                  <Box display="flex" justifyContent="space-between" alignItems="start">
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>{opt.label}</Typography>
                      <Typography variant="h6" color="primary" fontWeight={600}>{formatDate(opt.proposedDate)}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {opt.startTime} – {opt.endTime}{opt.location && ` · ${opt.location}`}
                      </Typography>
                    </Box>
                    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
                      <AvailabilityBadge status={opt.availabilityStatus} />
                      {opt.isAutoProposed && <Chip label="Auto" size="small" color="secondary" variant="outlined" />}
                      {!isCompleted && <>
                        <Tooltip title="Edit option">
                          <IconButton size="small" onClick={() => openEdit(opt)}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Delete option">
                          <IconButton size="small" color="error" onClick={() => handleDeleteOption(opt.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </>}
                    </Box>
                  </Box>
                  <ConflictBox conflictSummary={opt.conflictSummary} />
                  {opt.notes && (
                    <Box mt={1} sx={{ bgcolor: '#E8F5E9', border: '1px solid #C8E6C9', borderRadius: 1, px: 1.5, py: 0.75 }}>
                      <Typography variant="caption" fontWeight={700} color="success.dark" display="block">
                        📋 Reason / Notes
                      </Typography>
                      <Typography variant="caption" color="text.primary" display="block">
                        {opt.notes}
                      </Typography>
                    </Box>
                  )}
                  {!isActive && !isCompleted && (
                    <Box mt={1.5}>
                      <Button size="small"
                        variant={opt.availabilityStatus === 'all_available' ? 'contained' : 'outlined'}
                        color={opt.availabilityStatus === 'conflict' ? 'warning' : 'success'}
                        startIcon={<ConfirmIcon />}
                        onClick={() => handleConfirm(selected.shootPlan.id, opt.id, opt)}>
                        {selected.shootPlan.status === 'confirmed' && selected.shootPlan.confirmedOptionId
                          ? `Switch to ${opt.label}` : `Confirm ${opt.label}`}
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })
          )}
        </Stack>
      )}
    </Box>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Shoot Planner</Typography>
          <Typography variant="body2" color="text.secondary">Plan and schedule shoots with crew availability checks</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchContents}>Refresh</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {proposalResult && <Alert severity={proposalResult.startsWith('No plans') ? 'warning' : 'success'} sx={{ mb: 2 }} onClose={() => setProposalResult(null)}>{proposalResult}</Alert>}

      {/* Filters */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <TextField
          size="small" label="Search title" value={searchFilter}
          onChange={e => setSearchFilter(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <TextField
          select size="small" label="Type" value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All Types</MenuItem>
          <MenuItem value="Long Video">Long Video</MenuItem>
          <MenuItem value="Shorts">Shorts</MenuItem>
          <MenuItem value="Ad Content">Ad Content</MenuItem>
        </TextField>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={2}>
          {/* Left panel — content list */}
          <Grid item xs={12} md={3} sx={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
            <Card variant="outlined">
              {/* Tabs */}
              <Tabs value={leftTab} onChange={(_, v) => setLeftTab(v)} variant="fullWidth"
                sx={{
                  borderBottom: '2px solid #e0e0e0',
                  '& .MuiTab-root': { fontSize: '0.72rem', minHeight: 42, fontWeight: 600, textTransform: 'none', color: 'text.secondary' },
                  '& .Mui-selected': { color: '#1a237e' },
                  '& .MuiTabs-indicator': { height: 3, bgcolor: '#1a237e', borderRadius: '3px 3px 0 0' },
                }}>
                <Tab label={`Awaiting (${applyFilters(pending).length})`} />
                <Tab label={`Confirmed (${applyFilters(confirmed).length})`} />
                <Tab label={`Done (${applyFilters(completed).length})`} />
              </Tabs>
              <CardContent sx={{ p: 1.5 }}>

                {/* Tab 0 — Awaiting Plan */}
                {leftTab === 0 && (
                  <Stack spacing={1}>
                    {applyFilters(pending).length === 0 && (
                      <Typography variant="body2" color="text.secondary" py={2} textAlign="center">All content has plans ✓</Typography>
                    )}
                    {applyFilters(pending).map(c => (
                      <Box key={c.id} onClick={() => setSelected(c)} sx={{
                        p: 1.5, borderRadius: 1, cursor: 'pointer', border: '1px solid',
                        borderColor: selected?.id === c.id ? 'primary.main' : 'divider',
                        bgcolor: selected?.id === c.id ? 'primary.50' : 'background.paper',
                        '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }
                      }}>
                        <Box display="flex" justifyContent="space-between" alignItems="start">
                          <Box flex={1}>
                            <Typography variant="body2" fontWeight={600} noWrap>{c.contentTitle}</Typography>
                            <Typography variant="caption" color="text.secondary">{c.contentId}</Typography>
                          </Box>
                          <Chip label={c.contentType === 'Long Video' ? 'LV' : 'Short'} size="small"
                            sx={{ bgcolor: CONTENT_TYPE_COLOR[c.contentType] || '#666', color: 'white', fontSize: '0.6rem' }} />
                        </Box>
                        <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
                          <Chip label={c.status} size="small" variant="outlined" color="warning" sx={{ fontSize: '0.65rem' }} />
                          {c.shootPlan && (
                            <Chip label={`${c.shootPlan.options?.length || 0} option(s)`} size="small" color="info" sx={{ fontSize: '0.65rem' }} />
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}

                {/* Tab 1 — Confirmed */}
                {leftTab === 1 && (
                  <Stack spacing={1}>
                    {applyFilters(confirmed).length === 0 && (
                      <Typography variant="body2" color="text.secondary" py={2} textAlign="center">No confirmed shoots</Typography>
                    )}
                    {applyFilters(confirmed).map(c => (
                      <Box key={c.id} onClick={() => setSelected(c)} sx={{
                        p: 1, borderRadius: 1, cursor: 'pointer',
                        bgcolor: selected?.id === c.id ? '#e8f5e9' : '#f1f8e9',
                        border: '1px solid',
                        borderColor: selected?.id === c.id ? 'success.main' : '#c8e6c9',
                        '&:hover': { borderColor: 'success.main' }
                      }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{c.contentTitle}</Typography>
                        <Typography variant="caption" color="text.secondary">{c.contentId}</Typography>
                        <Typography variant="caption" color="success.main" display="block">
                          Shoot: {formatDate(c.shootDate) || '—'}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}

                {/* Tab 2 — Completed */}
                {leftTab === 2 && (
                  <Stack spacing={1}>
                    {applyFilters(completed).length === 0 && (
                      <Typography variant="body2" color="text.secondary" py={2} textAlign="center">No completed shoots</Typography>
                    )}
                    {applyFilters(completed).map(c => (
                      <Box key={c.id} onClick={() => setSelected(c)} sx={{
                        p: 1, borderRadius: 1, cursor: 'pointer',
                        bgcolor: selected?.id === c.id ? '#E3F2FD' : '#F3F4F6',
                        border: '1px solid',
                        borderColor: selected?.id === c.id ? 'primary.main' : '#ccc',
                        '&:hover': { borderColor: 'primary.main' }
                      }}>
                        <Box display="flex" justifyContent="space-between" alignItems="start">
                          <Box flex={1}>
                            <Typography variant="body2" fontWeight={600} noWrap>{c.contentTitle}</Typography>
                            <Typography variant="caption" color="text.secondary">{c.contentId}</Typography>
                          </Box>
                          <Chip label="✓ Done" size="small" sx={{ bgcolor: '#4CAF50', color: '#fff', fontSize: '0.6rem' }} />
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
                          Shoot: {formatDate(c.shootDate) || '—'}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}

              </CardContent>
            </Card>
          </Grid>

          {/* Right panel — plan options (desktop only, hidden when maximized) */}
          {!isMobile && !maximized && (
            <Grid item xs={12} md={9} ref={rightPanelRef} sx={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
              {!selected ? (
                <Card variant="outlined" sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box textAlign="center" py={8} color="text.secondary">
                    <CalendarIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                    <Typography variant="body1" mt={1}>Select a content item to view or manage its shoot plan</Typography>
                  </Box>
                </Card>
              ) : (
                <Card variant="outlined">
                  <CardContent>
                    { renderPlanDetail() }
                  </CardContent>
                </Card>
              )}
            </Grid>
          )}

          {/* Maximized overlay — no Dialog/history, pure CSS fixed overlay */}
          {!isMobile && maximized && selected && (
            <Box sx={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              zIndex: 1300,
              bgcolor: 'background.default',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pt: 1, pb: 0.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}>
                <Button size="small" variant="outlined" startIcon={<FullscreenExitIcon />}
                  onClick={() => setMaximized(false)}>
                  Minimize
                </Button>
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                { renderPlanDetail() }
              </Box>
            </Box>
          )}

          {/* Mobile: plan detail shown as full-screen dialog on item tap */}
          {isMobile && selected && (
            <Dialog open fullScreen onClose={() => setSelected(null)}>
              <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Box>
                  <Typography variant="h6" fontWeight={700} noWrap>{selected.contentTitle}</Typography>
                  <Typography variant="caption" color="primary">{selected.contentId}</Typography>
                </Box>
                <IconButton onClick={() => setSelected(null)}><CloseIcon /></IconButton>
              </DialogTitle>
              <DialogContent dividers sx={{ p: 2 }}>
                { renderPlanDetail() }
              </DialogContent>
            </Dialog>
          )}

        </Grid>
      )}

      {/* Add / Edit Option Dialog */}
      <Dialog open={dialog.open} onClose={() => setDialog(d => ({ ...d, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog.mode === 'edit' ? `Edit ${dialog.option?.label}` : 'Add Plan Option'} — {selected?.contentTitle}
        </DialogTitle>
        <DialogContent>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              {(() => {
                const today = new Date();
                const minDate = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
                const max = new Date(); max.setDate(max.getDate() + 10);
                const maxDate = `${max.getFullYear()}-${String(max.getMonth()+1).padStart(2,'0')}-${String(max.getDate()).padStart(2,'0')}`;
                return (
                  <TextField fullWidth required label="Proposed Shoot Date" type="date"
                    value={form.proposedDate}
                    onChange={e => setForm(f => ({ ...f, proposedDate: e.target.value }))}
                    inputProps={{ min: minDate, max: maxDate }}
                    helperText={`Today to ${maxDate}`}
                    InputLabelProps={{ shrink: true }} />
                );
              })()}
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Start Time" type="time"
                value={form.startTime}
                onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="End Time" type="time"
                value={form.endTime}
                onChange={e => setForm(f => ({ ...f, endTime: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Location" value={form.location}
                onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2}
                label={dialog.mode === 'edit' ? 'Reason for Change *' : 'Notes / Reason'}
                placeholder={dialog.mode === 'edit' ? 'Why is the schedule being changed?' : 'Optional notes or reason'}
                required={dialog.mode === 'edit'}
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                error={dialog.mode === 'edit' && saving && !form.notes}
                helperText={dialog.mode === 'edit' && saving && !form.notes ? 'Reason is required when editing a plan' : ''}
              />
            </Grid>

            {/* Conflict preview after save */}
            {preview && (
              <Grid item xs={12}>
                <Alert severity={preview.status === 'all_available' ? 'success' : preview.status === 'conflict' ? 'error' : 'warning'}>
                  <Typography variant="caption" fontWeight={700}>
                    {STATUS_CHIP[preview.status]?.label || preview.status}
                  </Typography>
                  {preview.conflicts?.map((c, i) => (
                    <Typography key={i} variant="caption" display="block">
                      ❌ {c.name} ({c.role}) — {c.reason}
                    </Typography>
                  ))}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(d => ({ ...d, open: false }))}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.proposedDate}>
            {saving ? 'Saving & Checking…' : dialog.mode === 'edit' ? 'Save & Re-check' : 'Add & Check Availability'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ShootPlanner;
